import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Search, Phone, Mail, Check } from "lucide-react";
import {
  listGuides,
  createGuide,
  updateGuide,
  deleteGuide,
  GUIDE_STATUSES,
  GUIDE_GENDERS,
  type Guide,
  type GuideWrite,
  type GuideStatus,
} from "@/lib/guides-api";

type Draft = {
  id?: string;
  full_name: string;
  phone: string;
  email: string;
  area: string;
  gender: string;
  experience_years: number;
  charges_per_day: number;
  languages: string;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  internal_notes: string;
  status: GuideStatus;
  is_active: boolean;
};

const EMPTY: Draft = {
  full_name: "",
  phone: "",
  email: "",
  area: "",
  gender: "male",
  experience_years: 0,
  charges_per_day: 500,
  languages: "English",
  bio_en: "",
  bio_hi: "",
  bio_gu: "",
  internal_notes: "",
  status: "approved",
  is_active: true,
};

const STATUS_STYLE: Record<GuideStatus, string> = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-primary/12 text-primary",
  rejected: "bg-destructive/10 text-destructive",
};

export const Route = createFileRoute("/admin/guides")({ component: GuidesAdmin });

function GuidesAdmin() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<GuideStatus | "all">("all");
  const [edit, setEdit] = useState<Draft | null>(null);

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-guides"],
    queryFn: listGuides,
  });

  const toWrite = (d: Draft): GuideWrite => ({
    full_name: d.full_name.trim(),
    phone: d.phone.trim(),
    email: d.email.trim(),
    area: d.area.trim(),
    gender: d.gender,
    experience_years: Number.isFinite(d.experience_years) ? d.experience_years : 0,
    charges_per_day: Number.isFinite(d.charges_per_day) ? d.charges_per_day : 0,
    languages: d.languages
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean),
    bio_en: d.bio_en.trim(),
    bio_hi: d.bio_hi.trim(),
    bio_gu: d.bio_gu.trim(),
    internal_notes: d.internal_notes.trim(),
    status: d.status,
    is_active: d.is_active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-guides"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: Draft) => createGuide(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Guide created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: Draft) => updateGuide(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Guide updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteGuide(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });
  const statusMut = useMutation({
    mutationFn: ({ g, status }: { g: Guide; status: GuideStatus }) =>
      updateGuide(g.id, { ...toWrite(toDraft(g)), status }),
    onSuccess: () => {
      invalidate();
      toast.success("Status updated");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!edit.full_name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };

  const remove = (id: string) => {
    if (confirm("Delete this guide?")) {
      deleteMut.mutate(id);
    }
  };

  const counts = {
    pending: list.filter((g) => g.status === "pending").length,
    approved: list.filter((g) => g.status === "approved").length,
    rejected: list.filter((g) => g.status === "rejected").length,
    all: list.length,
  };

  const filtered = list
    .filter((g) => (tab === "all" ? true : g.status === tab))
    .filter((g) =>
      (g.full_name + g.phone + g.email + g.area + g.languages.join(" "))
        .toLowerCase()
        .includes(q.toLowerCase()),
    );

  return (
    <div className="min-w-0 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl font-bold">Guide Master</h2>
          <p className="text-sm text-muted-foreground">Internal roster of professional guides</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="rounded-full border border-border bg-card py-2 pl-9 pr-4 text-sm outline-none focus:border-primary"
            />
          </div>
          <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
            <Plus className="h-4 w-4" /> Add Guide
          </button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="inline-flex flex-wrap rounded-full border border-border bg-card p-1 text-sm">
        {(
          [
            { key: "all", label: "All", count: counts.all },
            { key: "approved", label: "Approved", count: counts.approved },
            { key: "rejected", label: "Rejected", count: counts.rejected },
            { key: "pending", label: "Pending", count: counts.pending },
          ] as const
        ).map((tp) => (
          <button
            key={tp.key}
            onClick={() => setTab(tp.key)}
            className={`rounded-full px-4 py-1.5 font-semibold transition-colors ${
              tab === tp.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {tp.label}
            <span
              className={`ml-1.5 rounded-full px-1.5 text-[10px] ${
                tab === tp.key ? "bg-primary-foreground/20" : "bg-muted"
              }`}
            >
              {tp.count}
            </span>
          </button>
        ))}
      </div>

      <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Area</th>
                <th className="p-3">Experience</th>
                <th className="p-3">Charges/day</th>
                <th className="p-3">Languages</th>
                <th className="p-3">Status</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                    Loading guides…
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-destructive">
                    {error instanceof Error ? error.message : "Failed to load guides"}
                  </td>
                </tr>
              )}
              {!isLoading && !isError && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                    No {tab === "all" ? "" : tab} guides
                  </td>
                </tr>
              )}
              {filtered.map((g) => (
                <tr key={g.id} className="border-t border-border hover:bg-muted/40">
                  <td className="p-3">
                    <div className="font-semibold">{g.full_name}</div>
                    {g.internal_notes ? (
                      <div className="text-[10px] text-muted-foreground">{g.internal_notes}</div>
                    ) : null}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-0.5 text-xs">
                      {g.phone ? (
                        <a
                          href={`tel:${g.phone}`}
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Phone className="h-3 w-3" /> {g.phone}
                        </a>
                      ) : null}
                      {g.email ? (
                        <a
                          href={`mailto:${g.email}`}
                          className="inline-flex items-center gap-1 text-muted-foreground hover:underline"
                        >
                          <Mail className="h-3 w-3" /> {g.email}
                        </a>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{g.area || "—"}</td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    {g.experience_years
                      ? `${g.experience_years} yr${g.experience_years === 1 ? "" : "s"}`
                      : "—"}
                  </td>
                  <td className="p-3 text-muted-foreground whitespace-nowrap">
                    ₹{g.charges_per_day}
                  </td>
                  <td className="p-3">
                    {g.languages.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {g.languages.map((l, i) => (
                          <span key={i} className="rounded-full bg-muted px-2 py-0.5 text-[11px]">
                            {l}
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col items-start gap-1">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold capitalize ${STATUS_STYLE[g.status]}`}
                      >
                        {g.status}
                      </span>
                      {!g.is_active ? (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                          Inactive
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {g.status === "pending" ? (
                        <>
                          <button
                            onClick={() => statusMut.mutate({ g, status: "approved" })}
                            disabled={statusMut.isPending}
                            title="Approve"
                            className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                          >
                            <Check className="h-3.5 w-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => statusMut.mutate({ g, status: "rejected" })}
                            disabled={statusMut.isPending}
                            title="Reject"
                            className="inline-flex items-center gap-1 rounded-lg border border-destructive/30 px-2 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                          >
                            <X className="h-3.5 w-3.5" /> Reject
                          </button>
                        </>
                      ) : null}
                      <button
                        onClick={() => setEdit(toDraft(g))}
                        title="Edit"
                        className="rounded-lg p-1.5 hover:bg-muted"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(g.id)}
                        title="Delete"
                        className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <Editor
          draft={edit}
          setDraft={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function toDraft(g: Guide): Draft {
  return {
    id: g.id,
    full_name: g.full_name,
    phone: g.phone,
    email: g.email,
    area: g.area,
    gender: g.gender || "male",
    experience_years: g.experience_years,
    charges_per_day: g.charges_per_day,
    languages: g.languages.join(", "),
    bio_en: g.bio_en,
    bio_hi: g.bio_hi,
    bio_gu: g.bio_gu,
    internal_notes: g.internal_notes,
    status: g.status,
    is_active: g.is_active,
  };
}

function Editor({
  draft,
  setDraft,
  onSave,
  onClose,
  saving,
}: {
  draft: Draft;
  setDraft: (d: Draft) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{draft.id ? "Edit" : "Add"} Guide</h3>
          <button onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <F label="Name" required>
            <input
              className="inp"
              value={draft.full_name}
              onChange={(e) => u({ full_name: e.target.value })}
            />
          </F>
          <F label="Phone">
            <input
              className="inp"
              value={draft.phone}
              onChange={(e) => u({ phone: e.target.value })}
            />
          </F>
          <F label="Email">
            <input
              type="email"
              className="inp"
              value={draft.email}
              onChange={(e) => u({ email: e.target.value })}
            />
          </F>
          <F label="Area">
            <input
              className="inp"
              value={draft.area}
              onChange={(e) => u({ area: e.target.value })}
            />
          </F>
          <F label="Gender">
            <select
              className="inp"
              value={draft.gender}
              onChange={(e) => u({ gender: e.target.value })}
            >
              {GUIDE_GENDERS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </F>
          <F label="Status">
            <select
              className="inp"
              value={draft.status}
              onChange={(e) => u({ status: e.target.value as GuideStatus })}
            >
              {GUIDE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </F>
          <F label="Languages (comma)">
            <input
              className="inp"
              value={draft.languages}
              onChange={(e) => u({ languages: e.target.value })}
            />
          </F>
          <F label="Experience (years)">
            <input
              type="number"
              min={0}
              className="inp"
              value={draft.experience_years}
              onChange={(e) => u({ experience_years: e.target.value ? +e.target.value : 0 })}
            />
          </F>
          <F label="Charges per day (₹)">
            <input
              type="number"
              min={0}
              className="inp"
              value={draft.charges_per_day}
              onChange={(e) => u({ charges_per_day: e.target.value ? +e.target.value : 0 })}
            />
          </F>
          <F label="Active">
            <select
              className="inp"
              value={String(draft.is_active)}
              onChange={(e) => u({ is_active: e.target.value === "true" })}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </F>
          <F label="Bio (EN)" full>
            <textarea
              rows={3}
              className="inp"
              value={draft.bio_en}
              onChange={(e) => u({ bio_en: e.target.value })}
            />
          </F>
          <F label="Bio (HI)" full>
            <textarea
              rows={3}
              className="inp"
              value={draft.bio_hi}
              onChange={(e) => u({ bio_hi: e.target.value })}
            />
          </F>
          <F label="Bio (GU)" full>
            <textarea
              rows={3}
              className="inp"
              value={draft.bio_gu}
              onChange={(e) => u({ bio_gu: e.target.value })}
            />
          </F>
          <F label="Internal Notes" full>
            <textarea
              rows={3}
              className="inp"
              value={draft.internal_notes}
              onChange={(e) => u({ internal_notes: e.target.value })}
            />
          </F>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {/* Inputs sit on --card (near-white) rather than --background (cream) so they read as
          clean white fields against the modal, matching the reference design. */}
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--card));border-radius:.75rem;padding:.55rem .75rem;font-size:.875rem;color:hsl(var(--foreground));outline:none;transition:border-color .15s}.inp:focus{border-color:hsl(var(--primary))}.inp::placeholder{color:hsl(var(--muted-foreground))}`}</style>
    </div>
  );
}

function F({
  label,
  required,
  full,
  children,
}: {
  label: string;
  required?: boolean;
  full?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
